import {Injectable} from '@angular/core';
import {AuthenticationService} from '../authenticationModule/services/authentication.service';
import {UtilsService} from '../appSharedModule/services/utils.service';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import PubSub, {AWSIoTProvider} from '@aws-amplify/pubsub';
import Auth from '@aws-amplify/auth';
import {saveAs} from 'file-saver';
import {BehaviorSubject, firstValueFrom, ReplaySubject, Subscription} from 'rxjs';
import {AwsServiceType} from '../Enum/aws-service-type.enum';
import {Etree} from '../models/Etree';
import {Md5} from 'ts-md5';
import * as _ from 'lodash';
import {ConfirmationService} from 'primeng/api';

@Injectable()
export class AwsService {
  awsPortalKeys = null;
  awsMqttKeys = null;
  awsPortalPortsCountersActivationDate = null;
  awsPortalPortsCountersIsActivated = false;
  awsMqttPortsCountersActivationDate = null;
  awsMqttEtreesPlrLatActivationDate = null;
  awsPortalEtreesCountersActivationDate = null;
  awsPortalEtreesCountersIsActivated = false;
  awsMqttEtreesCountersActivationDate = null;
  awsTimeSyncMonitoringIsActivated = false;
  awsTimeSyncMonitoringActivationDate = null;
  pythonSampleHost = 'xxxxxxxxxx-ats.iot.us-east-2.amazonaws.com';
  pythonSampleCompanyId = 'xxxxxxxxxxxxxxxxxxxxxxx';
  retryAwsConnectionNb = 0;
  awsIsConnected = false;
  boundedRefreshToken = null;
  companyIdGlob = null;
  iotThingNameGlob = null;
  loadingStatus = 'Not connected';
  awsLiveDataObservable = null;
  subscriptionsAws: Subscription[] = [];
  awsConfigDataReceived = false;
  awsTimeSyncConfigDataReceived = false;
  crosscosData: any;
  etreesData: any;
  plrLatData: any;
  graphsOptions: any;
  counterDataUpdatedSource$$ = new BehaviorSubject<any>(null);
  counterDataUpdated$ = this.counterDataUpdatedSource$$.asObservable();
  portsCountersGenericFields: any;
  etreesCountersGenericFields: any;
  fieldsNameForRawValueInHistory: any;

  constructor(
    public authenticationService: AuthenticationService,
    protected utilsService: UtilsService,
    protected http: HttpClient,
    protected confirmationService: ConfirmationService,
  ) {
    this.boundedRefreshToken = this.refreshToken.bind(this);
    this.loadingStatus = 'Waiting for server authentication...';
    this.graphsOptions = {
      responsive: true,
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      scales: {
        x: [{
          type: 'time',
        }],
        y: [{
          ticks: {
            fontSize: 10,
            beginAtZero: false,
          },
        }],
      },
    };

    this.portsCountersGenericFields = [
      {code: 'rx_b', unit: 'Bytes', type: 'rx', label: 'Input Bytes'},
      {code: 'rx_p', unit: 'Packets', type: 'rx', label: 'Input Packets'},
      {code: 'rx_e_p', unit: 'Packets', type: 'rx', label: 'Input Errors Packets'},
      {code: 'rx_fcs_e_p', unit: 'Packets', type: 'rx', label: 'Input Bad FCS Packets'},
      {code: 'rx_drop_p', unit: 'Packets', type: 'rx', label: 'Input Dropped Packets'},
      {code: 'rx_crc_e_p', unit: 'Packets', type: 'rx', label: 'Inner CRC Errors Packets'},
      {code: 'rx_mac_e_p', unit: 'Packets', type: 'rx', label: 'Input Incorrect MAC Packets'},
      {code: 'tx_b', unit: 'Bytes', type: 'tx', label: 'Output Bytes'},
      {code: 'tx_p', unit: 'Packets', type: 'tx', label: 'Output Packets'},
    ];
    this.etreesCountersGenericFields = [
      {code: 'rx_b', unit: 'Bytes', type: 'rx', label: 'Input Bytes'},
      {code: 'rx_p', unit: 'Packets', type: 'rx', label: 'Input Packets'},
      {code: 'rx_e_p', unit: 'Packets', type: 'rx', label: 'Input Errors Packets'},
      {code: 'rx_fcs_e_p', unit: 'Packets', type: 'rx', label: 'Input Bad FCS Packets'},
      {code: 'rx_drop_p', unit: 'Packets', type: 'rx', label: 'Input Dropped Packets'},
      {code: 'rx_mac_e_p', unit: 'Packets', type: 'rx', label: 'Input Incorrect MAC Packets'},
      {code: 'rx_hc_p', unit: 'Packets', type: 'rx', label: 'Input Header Compressed Packets'},
      {code: 'rx_pr_p', unit: 'Packets', type: 'rx', label: 'Input Padding Removal Packets'},
      {code: 'rx_ms_e_p', unit: 'Packets', type: 'rx', label: 'Input Size Error Packets'},
      {code: 'rx_pr_e_p', unit: 'Packets', type: 'rx', label: 'Input Padding Removal Error Packets'},
      {code: 'rx_bo_e_p', unit: 'Packets', type: 'rx', label: 'Input Buffer Overflow Error Packets'},
      {code: 'tx_b', unit: 'Bytes', type: 'tx', label: 'Output Bytes'},
      {code: 'tx_p', unit: 'Packets', type: 'tx', label: 'Output Packets'},
      {code: 'rx_rec_p', unit: 'Packets', type: 'rx', label: 'Input Recovered Packets'},
    ];
    this.fieldsNameForRawValueInHistory = [
      'i_st', 'lat', 'plr', 'tx_pow', 'rx_pow', 'sp',
    ];

    this.resetSeriesValues();
  }

  public resetSeriesValues() {
    this.crosscosData = {
      receivedDataNumber: 0,
      dataHistory: {},
      lastLoadedData: null,
    };
    this.etreesData = {
      receivedDataNumber: 0,
      dataHistory: {},
      lastLoadedData: null,
    };
    this.plrLatData = {
      receivedDataNumber: 0,
      dataHistory: {},
      lastLoadedData: null,
    };
  }

  public updateAwsParametersFromServer() {
    const prom = this.getPortalParametersForMqtt();
    prom.then((response) => {
        this.updateLocalParametersFromServerParameters(response);
      }
    );
    return prom;
  }

  public updateAwsTimeSyncParametersFromServer() {
    const prom = this.getPortalParametersForTimeSyncMqtt();
    prom.then((response) => {
        this.updateTimeSyncLocalParametersFromServerParameters(response);
      }
    );
    return prom;
  }

  public updateLocalParametersFromServerParameters(response) {
    if (!response) {
      console.warn('updateLocalParametersFromServerParameters: missing data:', response);
      return;
    }
    this.awsConfigDataReceived = true;
    this.awsMqttKeys = response.awsMqttKeys;
    this.awsMqttPortsCountersActivationDate = response.awsMqttPortsCountersActivationDate;
    this.awsMqttEtreesCountersActivationDate = response.awsMqttEtreesCountersActivationDate;
    this.awsMqttEtreesPlrLatActivationDate = response.awsMqttEtreesPlrLatActivationDate;
    this.awsPortalKeys = response.awsPortalKeys;
    this.awsPortalPortsCountersActivationDate = response.awsPortalPortsCountersActivationDate;
    this.awsPortalPortsCountersIsActivated = response.awsPortalPortsCountersIsActivated;
    this.awsPortalEtreesCountersActivationDate = response.awsPortalEtreesCountersActivationDate;
    this.awsPortalEtreesCountersIsActivated = response.awsPortalEtreesCountersIsActivated;
    if (this.awsMqttKeys) {
      this.pythonSampleHost = this.awsMqttKeys['awsMqttHost'];
      this.pythonSampleCompanyId = this.awsMqttKeys['companyId'];
    }
    if (this.companyIdGlob && this.companyIdGlob !== response.companyId) { // for impersonation case !
      this.companyIdGlob = response.companyId;
      this.resetSeriesValues();
      this.awsIsConnected = false;
      this.retryAwsConnectionNb = 0;
      this.removeSubscriptionsAws();
      this.utilsService.saveUserData('cognitoIdentityId', null); // remove data from local storage to get new auth
      this.awsIsConnected = false;
      this.initAwsSubscription();
    }
  }

  public updateTimeSyncLocalParametersFromServerParameters(response) {
    this.awsConfigDataReceived = true;
    this.awsMqttKeys = response.awsMqttKeys;
    this.awsTimeSyncMonitoringActivationDate = response.awsTimeSyncMonitoringActivationDate;
    this.awsTimeSyncMonitoringIsActivated = response.awsTimeSyncMonitoringIsActivated;
    this.awsPortalKeys = response.awsPortalKeys;
    if (this.awsMqttKeys) {
      this.pythonSampleHost = this.awsMqttKeys['awsMqttHost'];
      this.pythonSampleCompanyId = this.awsMqttKeys['companyId'];
    }
  }

  public async getPortalParametersForMqtt() {
    const promise = firstValueFrom(this.http.get<any>(environment.baseUrlWs + '/api/client/' + 'xxxxx' + '/get_portal_parameters_for_mqtt'));
    promise.then(response => {
      this.updateLocalParametersFromServerParameters(response);
    }).catch(error => {
      this.utilsService.handleErrorWebservice$(error, 'Error in transmitting your request. Please contact site admin');
    });
    return promise;
  }

  public async getPortalParametersForTimeSyncMqtt() {
    const promise = firstValueFrom(this.http.get<any>(environment.baseUrlWs + '/api/time-sync-monitoring-params'));
    promise.then(response => {
      this.updateLocalParametersFromServerParameters(response);
    }).catch(error => {
      this.utilsService.handleErrorWebservice$(error, 'Error in transmitting your request. Please contact site admin');
    });
    return promise;
  }

  async initAwsSubscription() {
    if (this.awsIsConnected) {
      return;
    }
    this.retryAwsConnectionNb += 1;
    if (!this.awsPortalKeys) {
      return;
    }

    const region = this.awsPortalKeys['region'];
    const awsEndpoint = this.awsPortalKeys['awsMqttHost'];
    const identityPoolId = this.awsPortalKeys['identityPoolId'];

    PubSub.addPluggable(new AWSIoTProvider({
      aws_pubsub_region: region,
      aws_pubsub_endpoint: 'wss://' + awsEndpoint + '/mqtt',
    }));

    Auth.configure({
      region: region,
      identityPoolId: identityPoolId,
      refreshHandlers: {
        'developer': this.boundedRefreshToken // the property could be 'google', 'facebook', 'amazon', 'developer', OpenId domain  // https://docs.amplify.aws/lib/auth/advanced/q/platform/js#token-refresh
      }
    });

    this.iotThingNameGlob = null;
    const currentCredentials = await this.getCognitoTokenFromLocalStorage();
    if (currentCredentials && currentCredentials.userId && currentCredentials.iotThingName) {
      this.companyIdGlob = currentCredentials.companyId;
    } else {
      this.loadingStatus = 'Waiting for AWS Cognito authentication ...';
      const {
        token,
        identityId,
        expiresAt,
        cognitoIdentityId,
        companyId
      } = await this.getCognitoTokenFromPortalServer();
      this.companyIdGlob = companyId;
      this.iotThingNameGlob = 'iot' + companyId;
      this.loadingStatus = 'Loading AWS authentication data ...';
      const cognitoTokenData = {
        token: token,
        identity_id: identityId,
        expires_at: expiresAt,
      };
      const user = {name: cognitoIdentityId};
      await Auth.federatedSignIn(
        'developer',
        cognitoTokenData,
        user,
      );
    }

    this.subscribeToAwsService();
  }

  async refreshToken() {
    // refresh the token here and get the new token info
    const {token, identityId} = await this.getCognitoTokenFromPortalServer();

    return new Promise((resolve) => {
      const data = {
        token, // the token from the provider
        identity_id: identityId, // optional, the identityId for the credentials
        expires_at: new Date().getTime() + 1000 * 60 * 2,
      };
      resolve(data);
    });
  }

  async activatePortalPub(awsServiceType) {
    const prom = firstValueFrom(this.http.get<any>(environment.baseUrlWs + '/api/client/' + 'xxxxx' + '/activate_portal_data_distribution/' + awsServiceType));
    prom.then(response => {
      this.utilsService.displayMessage('Portal Data distribution activated');
      this.updateLocalParametersFromServerParameters(response);
      this.utilsService.unlockUi();
      this.initAwsSubscription();
    }).catch(error => {
      this.utilsService.handleErrorWebservice$(error, 'Error in transmitting your request. Please contact site admin');
    });
    return prom;
  }

  onClickActivateMqttPub(awsServiceType: AwsServiceType) {
    this.confirmationService.confirm({
      header: 'Activation Confirmation',
      message: 'Are you sure to enable data distribution of Connection Data through AWS MQTT? <br>',
      accept: () => {
        this.utilsService.lockUi();
        this.activateMqttAndGetCertificates(awsServiceType);
      },
      reject: () => {
      }
    });
  }

  onClickActivateTimeSyncMonitoring() {
    this.confirmationService.confirm({
      header: 'Activation Confirmation',
      message: 'Are you sure to enable data distribution of Connection Data through AWS MQTT? <br>',
      accept: () => {
        this.utilsService.lockUi();
        this.activateTimeSyncMonitoringAndGetCertificates();
      },
      reject: () => {
      }
    });
  }

  activateMqttAndGetCertificates(awsServiceType: AwsServiceType) {
    this.utilsService.lockUi();
    this.http.get<any>(environment.baseUrlWs + '/api/client/' + 'xxxxx' + '/activate_iot_data_distribution/' + awsServiceType).subscribe({
      next: response => {
        this.utilsService.displayMessage('Data distribution activated');
        this.updateLocalParametersFromServerParameters(response);
        this.utilsService.unlockUi();
      }, error: error => {
        this.utilsService.handleErrorWebservice$(error, 'Error in transmitting your request. Please contact site admin');
      }
    });
  }

  activateTimeSyncMonitoringAndGetCertificates() {
    this.utilsService.lockUi();
    this.http.get<any>(environment.baseUrlWs + '/api/activate-time-sync-monitoring').subscribe({
      next: response => {
        this.utilsService.displayMessage('Data distribution activated');
        this.updateTimeSyncLocalParametersFromServerParameters(response);
        this.utilsService.unlockUi();
      }, error: error => {
        this.utilsService.handleErrorWebservice$(error, 'Error in transmitting your request. Please contact site admin');
      }
    });
  }

  async onClickDeactivateMqttData(awsServiceType: AwsServiceType, dataTypeForClient) {
    this.confirmationService.confirm({
      header: 'Deactivation Confirmation',
      message: 'Are you sure to disable data distribution of ' + dataTypeForClient + ' through AWS MQTT ? <br>',
      accept: () => {
        this.utilsService.lockUi();
        this.deactivatePortalPub(awsServiceType);
      }
    });
  }

  async onClickDeactivateTimeSyncMonitoring() {
    this.confirmationService.confirm({
      header: 'Deactivation Confirmation',
      message: 'Are you sure to disable data distribution of Precision Time Exchange Monitoring through AWS MQTT ? <br>',
      accept: () => {
        this.utilsService.lockUi();
        this.deactivateTimeSyncMonitoring();
      }
    });
  }

  public deactivatePortalPub(awsServiceType) {
    this.http.get<any>(environment.baseUrlWs + '/api/client/' + 'xxxxx' + '/deactivate_iot_data_distribution/' + awsServiceType).subscribe({
      next: response => {
        this.updateLocalParametersFromServerParameters(response);
        this.utilsService.unlockUi();
      }, error: error => {
        this.utilsService.handleErrorWebservice$(error, 'Error in transmitting your request. Please contact site admin');
      }
    });
  }

  public deactivateTimeSyncMonitoring() {
    this.http.get<any>(environment.baseUrlWs + '/api/deactivate-time-sync-monitoring').subscribe({
      next: response => {
        this.updateTimeSyncLocalParametersFromServerParameters(response);
        this.utilsService.unlockUi();
      }, error: error => {
        this.utilsService.handleErrorWebservice$(error, 'Error in transmitting your request. Please contact site admin');
      }
    });
  }

  subscribeToAwsService() {
    this.loadingStatus = 'Waiting for first live data ...';
    this.awsLiveDataObservable = PubSub.subscribe('iot' + this.companyIdGlob + '/#', {provider: 'AWSIoTProvider'}).subscribe({
      next: subData => {
        this.loadingStatus = 'Connected, receiving data ...';
        this.awsIsConnected = true;
        const countersData = subData.value;
        if (!countersData) {
          return;
        }
        this.retryAwsConnectionNb = 0;
        const topic = subData.value[Object.getOwnPropertySymbols(countersData)[0]]; // https://www.keithcirkel.co.uk/metaprogramming-in-es6-symbols/
        const serviceType = topic.split('/')[1];

        let seriesName = '??';
        if (serviceType === AwsServiceType.ETREES_IN_OUT_COUNTERS) {
          this.etreesData.lastLoadedData = countersData;
          this.etreesData.receivedDataNumber += 1;
          seriesName = 'etreesData';
          this.utilsService.saveUserData('lastEtreesDataForDebug', countersData);
        }

        if (serviceType === AwsServiceType.CROSSCOS_CONNECTIONS_COUNTERS) {
          this.crosscosData.lastLoadedData = countersData;
          this.crosscosData.receivedDataNumber += 1;
          seriesName = 'crosscosData';
          this.utilsService.saveUserData('lastCrosscosDataForDebug', countersData);
        }

        if (serviceType === AwsServiceType.ETREES_PLR_LAT_VALUES) {
          this.plrLatData.lastLoadedData = countersData;
          this.plrLatData.receivedDataNumber += 1;
          seriesName = 'plrLatData';
          this.utilsService.saveUserData('lastPlrLatDataForDebug', countersData);
        }

        this.counterDataUpdatedSource$$.next(seriesName);

        if (seriesName !== 'plrLatData') {
          // tslint:disable-next-line:forin
          for (const crosscoKey in this[seriesName].lastLoadedData) {
            const crosscoValues = this[seriesName].lastLoadedData[crosscoKey];
            const previousCrosscoValues = this[seriesName].previousLoadedData && this[seriesName].previousLoadedData[crosscoKey];
            for (const field in crosscoValues) {
              if (field === 't' || field === 'sfp_t') {
                continue;
              }
              let t = null;
              if (field === 'rx_pow' || field === 'tx_pow' || field === 'sp') {
                t = crosscoValues['sfp_t'];
              } else {
                t = crosscoValues['t'];
              }
              let value = null;
              if (this.fieldsNameForRawValueInHistory.includes(field)) {
                value = crosscoValues[field];
              } else {
                if (previousCrosscoValues) {
                  value = crosscoValues[field] - previousCrosscoValues[field];
                } else {
                  value = 0;
                }
              }
              if (!this[seriesName].dataHistory[crosscoKey]) {
                this[seriesName].dataHistory[crosscoKey] = {};
              }
              if (!this[seriesName].dataHistory[crosscoKey][field]) {
                this[seriesName].dataHistory[crosscoKey][field] = {
                  labels: [],
                  datasets: [{
                    label: field,
                    data: [],
                    cubicInterpolationMode: 'monotone',
                    tension: 0,
                  }]
                };
              }
              const dataHistoryElt = this[seriesName].dataHistory[crosscoKey][field];
              const dataset = dataHistoryElt.datasets[0];
              if (value !== null) {
                if (!dataHistoryElt.labels.includes(t)) {
                  dataHistoryElt.labels.push(t);
                  if (field === 'i_st') {
                    if (value === 'UP') {
                      value = 1;
                    } else {
                      value = 0;
                    }
                  }
                  dataset.data.push(value);
                }
              }
            }
          }
        } else {
          // tslint:disable-next-line:forin
          for (const etreeId in this.plrLatData.lastLoadedData) {
            const etreeValues = this.plrLatData.lastLoadedData[etreeId];
            // tslint:disable-next-line:forin
            for (const pop in etreeValues) {
              const popValues = etreeValues[pop];
              const t = popValues['time'];
              const key = etreeId + '_' + pop;
              if (!this.plrLatData.dataHistory[key]) {
                this.plrLatData.dataHistory[key] = {};
              }
              for (const field of ['lat', 'plr']) {
                const value = popValues[field];
                if (!this.plrLatData.dataHistory[key][field]) {
                  this.plrLatData.dataHistory[key][field] = {
                    labels: [],
                    datasets: [{
                      label: field,
                      data: [],
                      cubicInterpolationMode: 'monotone',
                      tension: 0,
                    }]
                  };
                }
                const dataHistoryElt = this[seriesName].dataHistory[key][field];
                const dataset = dataHistoryElt.datasets[0];
                if (!dataHistoryElt.labels.includes(t)) {
                  dataHistoryElt.labels.push(t);
                  dataset.data.push(value);
                }
              }
            }
          }
        }
        this[seriesName].previousLoadedData = _.clone(this[seriesName].lastLoadedData);
      },
      error: error => {
        this.loadingStatus = 'Not connected... error: ' + error.error.errorMessage;
        if (this.retryAwsConnectionNb <= 3) {
          this.retryAwsConnectionNb += 1;
          this.subscribeToAwsService();
        } else {
          this.utilsService.handleErrorWebservice$(error, 'Error connecting to MQTT via Cognito. Please contact site admin');
        }
      },
      complete: () => console.log('Done'),
    });
    this.subscriptionsAws.push(this.awsLiveDataObservable);
    this.loadingStatus = 'Connected, waiting for first data ...';
  }

  removeSubscriptionsAws() {
    for (const subscription of this.subscriptionsAws) {
      subscription.unsubscribe();
    }
  }

  onClickDownloadCertPem() {
    let fileContent = this.awsMqttKeys['certPem'];
    if (fileContent) {
      fileContent = new Blob([fileContent], {type: 'text'});
      saveAs(fileContent, 'certificate.pem.crt');
    }
  }

  onClickDownloadPrivateKey() {
    let fileContent = this.awsMqttKeys['privateKey'];
    if (fileContent) {
      fileContent = new Blob([fileContent], {type: 'text'});
      saveAs(fileContent, 'private.pem.key');
    }
  }

  onClickDownloadRootCat() {
    let fileContent = this.awsMqttKeys['rootCA'];
    if (fileContent) {
      fileContent = new Blob([fileContent], {type: 'text'});
      saveAs(fileContent, 'root-CA.crt');
    }
  }

  public loadEtreesForClientDisplay() {
    const replay = new ReplaySubject<any>();
    const etreesUrl = environment.baseUrlWs + '/api/bw_etrees';
    this.utilsService.cacheableObservable(() => this.http.get<any>(etreesUrl), 'etrees_aws_' + this.authenticationService.userAuthenticated.uniqid, false)
      .subscribe({
          next: (response) => {
            let etreesToDisplay = [];
            for (const etree of response) {
              const etreeData: any = JSON.parse(etree.jsonData) as Etree;
              delete etree.jsonData;
              if (!etreeData || etreeData.status === 'deprovisioned') {
                continue;
              }
              const clientEtree = {
                contractReference: etreeData.contractReference,
                clientReference: etreeData.clientReference,
                id: etreeData.id,
                injection: [],
                receptions: [],
              };
              if (etreeData.activeEtreeInjectionDemarcationPoint && etreeData.activeEtreeInjectionDemarcationPoint.activeClientConnectionPoints) {
                this.processCcps(etreeData.activeEtreeInjectionDemarcationPoint.activeClientConnectionPoints, clientEtree.injection, 'IN', etreeData.id);
              }
              for (const erp of etreeData.etreeReceptionDemarcationPoints) {
                if (erp.status === 'deprovisioned') {
                  continue;
                }
                this.processCcps(erp.activeClientConnectionPoints, clientEtree.receptions, 'OUT', etreeData.id);
              }
              etreesToDisplay.push(clientEtree);
            }
            etreesToDisplay = _.orderBy(etreesToDisplay, ['contractReference']);
            replay.next(etreesToDisplay);
          },
          error: (error: any) => {
            this.utilsService.handleErrorWebservice$(error);
          }
        }
      );
    return replay.asObservable();
  }

  public getCcpDetails(ccp: any, etreeId, way) {
    const portKey = this.makeCcpPortKey(ccp);
    const portKey3 = this.makeCcpPortKey3(ccp, etreeId, way);
    const ccpDetails = {
      key: portKey,
      key2: portKey,
      key3: portKey3,
      type: ccp.mbelAllocation,
      crossco: ccp.crossConnectId,
      injectionMethod: ccp.injectionMethod,
    };
    if (ccp.strandOrWave) {
      ccpDetails['strandOrWave'] = ccp.strandOrWave;
    }
    if (ccp.taggedFrames && ccp.clientVlan) {
      ccpDetails['vlan'] = ccp.clientVlan;
    }
    if (ccp.taggedFrames && ccp.mbelHeaderKey) {
      ccpDetails['hk'] = ccp.mbelHeaderKey;
    }
    return ccpDetails;
  }

  public makeCcpPortKey(ccp: any) {
    if (!ccp['crossConnectId']) {
      // ccp['_keyFull'] = null;
      ccp['_key'] = null;
      return null;
    }
    let fullKey = '' + ccp['crossConnectId'];
    let fullName = '' + ccp['crossConnectId'];
    if (ccp['connectorId'] && ccp['connectorId'].length) {
      fullKey += '_' + ccp['connectorId'];
      fullName += ' (' + ccp['connectorId'] + ')';
    }
    const md5 = new Md5();
    const md5Value = '' + md5.appendStr(fullKey).end();
    const key = md5Value.substr(0, 16);
    ccp['_fullName'] = fullName;
    ccp['_keyFull'] = fullName;
    ccp['_key'] = key;
    return key;
  }

  public makeCcpPortKey3(ccp: any, etreeId, way) {
    if (!ccp['crossConnectId']) {
      ccp['_key3Full'] = null;
      ccp['_key'] = null;
      return null;
    }
    let injectionMethod = '';
    if (way === 'OUT') {
      const injectionMethodRelated = ccp['relatedInjectionCcp'] && ccp['relatedInjectionCcp']['mbelAllocation'];
      if (injectionMethodRelated) {
        injectionMethod = injectionMethodRelated;
      } else {
        injectionMethod = ccp['mbelAllocation'];
      }
    }
    ccp['injectionMethod'] = injectionMethod;
    if (way === 'IN') { // set only ccp['injectionMethod'], not injectionMethod which is used in the key!!!
      ccp['injectionMethod'] = ccp['mbelAllocation'];
    }

    let fullKey = etreeId + '_' + way + '_' + injectionMethod + '_' + ccp['crossConnectId'];
    if (ccp['connectorId'] && ccp['connectorId'].length) {
      fullKey += '_' + ccp['connectorId'];
    }
    if (ccp['taggedFrames']) {
      fullKey += '_' + (ccp['clientVlan'] ? ccp['clientVlan'] : '') + '_' + (ccp['mbelHeaderKey'] ? ccp['mbelHeaderKey'] : '');
    }
    const md5 = new Md5();
    const md5Value = '' + md5.appendStr(fullKey).end();
    const key = md5Value.substr(0, 16);
    ccp['_key3Full'] = fullKey;
    ccp['_key3'] = key;
    return key;
  }

  private async getCognitoTokenFromLocalStorage() {
    let currentCredentials = null;
    const identityId = this.utilsService.loadUserPref('cognitoIdentityId');
    const companyId = this.utilsService.loadUserPref('companyId');
    const iotThingName = this.utilsService.loadUserPref('iotThingName');
    const awsAuthUserId = this.utilsService.loadUserPref('awsAuthUserId');
    if (!identityId || !companyId || !iotThingName || !awsAuthUserId || awsAuthUserId !== this.authenticationService.userAuthenticated.uniqid) {
      return null;
    }
    try {
      currentCredentials = await Auth.currentAuthenticatedUser();
    } catch (error) {
      return null;
    }
    const userId = currentCredentials.id;
    const token = currentCredentials.token as string;
    const expiresAt = JSON.parse(atob(token.split('.')[1])).exp;
    const expirationDate = (new Date().getTime() / 1000);
    const expireInMin = expirationDate - expiresAt;
    if (expiresAt && (expireInMin > 0) && identityId && identityId === userId && companyId && token) {
      return {token, identityId, expiresAt, userId, companyId, iotThingName};
    }
    return null;
  }

  private async getCognitoTokenFromPortalServer() {
    const data = await firstValueFrom(this.http.get<any>(environment.baseUrlWs + '/api/users/' + this.authenticationService.userCurrent.uniqid + '/get-aws-cognito-identity')).then(
      (response) => {
        return response;
      },
      (error) => {
        this.utilsService.handleErrorWebservice$(error, 'Error in transmitting your request. Please contact site admin');
      }
    );
    const identityId = data.IdentityId as string;
    const token = data.Token as string;
    const expiresAt = JSON.parse(atob(token.split('.')[1])).exp;
    const cognitoIdentityId = data.cognitoIdentityId;
    const companyId = data.CompanyId;
    const iotThingName = data.iotThingName;
    this.utilsService.saveUserData('cognitoIdentityId', cognitoIdentityId);
    this.utilsService.saveUserData('companyId', companyId);
    this.utilsService.saveUserData('iotThingName', iotThingName);
    this.utilsService.saveUserData('awsAuthUserId', this.authenticationService.userAuthenticated.uniqid);
    return {token, identityId, expiresAt, cognitoIdentityId, companyId, iotThingName};
  }

  private processCcps(activeClientConnectionPoints: any[], targetArray: any[], direction: string, etreeDataId: string) {
    const popCcps = {};
    if (activeClientConnectionPoints) {
      for (const ccp of activeClientConnectionPoints) {
        const popUuid = ccp.popTechnicalId;
        const ccpDetails = this.getCcpDetails(ccp, etreeDataId, direction);
        if (!popCcps[popUuid]) {
          popCcps[popUuid] = {pop: popUuid, ccps: []};
        }
        popCcps[popUuid].ccps.push({xco: ccpDetails});
      }
      const sortedList = _.orderBy(Object.values(popCcps), ['injectionMethod']);
      targetArray.push(...sortedList);
    }
  }

}
